import { useCallback, useMemo } from "preact/hooks";
import { useData } from "../hooks/useData";
import { useTimeRange, type TimeRange } from "../hooks/useTimeRange";
import { fetchSpeedtestHistory } from "../utils/api";
import { formatBps, formatMs, formatTimestamp, formatTimeAgo } from "../utils/format";
import { TimeRangePicker } from "../components/TimeRangePicker";
import { SkeletonLoader } from "../components/SkeletonLoader";
import { ErrorMessage } from "../components/ErrorMessage";
import { LastUpdated } from "../components/LastUpdated";
import { SpeedtestChart } from "../components/charts/SpeedtestChart";
import "./Speedtest.css";

const SPEEDTEST_RANGES = ["24h", "7d", "30d", "45d"] as const;

export default function Speedtest() {
  const { range, setRange } = useTimeRange("7d" as TimeRange);

  const fetchHistoryCb = useCallback(
    () => fetchSpeedtestHistory(range),
    [range]
  );
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
    refresh: refreshHistory,
  } = useData(fetchHistoryCb);

  const handleRangeChange = (r: string) => {
    if (SPEEDTEST_RANGES.includes(r as typeof SPEEDTEST_RANGES[number])) {
      setRange(r as TimeRange);
    }
  };

  const latestMikrotik = useMemo(() => {
    const items = history?.data.mikrotik ?? [];
    return items.length > 0 ? items[items.length - 1] : null;
  }, [history]);

  const latestOokla = useMemo(() => {
    const items = history?.data.ookla ?? [];
    return items.length > 0 ? items[items.length - 1] : null;
  }, [history]);

  const speedtestData = useMemo(
    () => ({
      mikrotik: history?.data.mikrotik ?? [],
      ookla: history?.data.ookla ?? [],
    }),
    [history]
  );

  const lastTimestamp = useMemo(() => {
    const candidates = [
      latestMikrotik?.timestamp,
      latestOokla?.timestamp,
    ].filter(Boolean) as string[];
    if (candidates.length === 0) return null;
    return candidates.sort()[candidates.length - 1];
  }, [latestMikrotik, latestOokla]);

  return (
    <div class="speedtest">
      <h1 class="speedtest__title">Speedtest</h1>

      {historyError && (
        <ErrorMessage message={historyError.message} onRetry={refreshHistory} />
      )}

      <section class="speedtest__latest card">
        <h2 class="speedtest__section-title">Hasil Terakhir</h2>
        <div class="speedtest__latest-grid">
          <div class="speedtest__result-card">
            <div class="speedtest__result-header">
              <h3 class="speedtest__result-source">MikroTik</h3>
              <span class="speedtest__badge speedtest__badge--mikrotik">
                MikroTik
              </span>
            </div>
            {historyLoading ? (
              <SkeletonLoader type="card" />
            ) : latestMikrotik ? (
              <>
                <p class="speedtest__result-value">
                  ↓ {formatBps(latestMikrotik.download)}
                </p>
                <p class="speedtest__result-detail">
                  ↑ {formatBps(latestMikrotik.upload)} ·{" "}
                  {formatMs(latestMikrotik.ping)}
                </p>
                <p class="speedtest__result-meta">
                  {latestMikrotik.server_name}
                </p>
                <p class="speedtest__result-meta">
                  {formatTimestamp(latestMikrotik.timestamp)} (
                  {formatTimeAgo(latestMikrotik.timestamp)})
                </p>
              </>
            ) : (
              <p class="speedtest__empty">Belum ada data</p>
            )}
          </div>

          <div class="speedtest__result-card">
            <div class="speedtest__result-header">
              <h3 class="speedtest__result-source">Ookla</h3>
              <span class="speedtest__badge speedtest__badge--ookla">
                Ookla
              </span>
            </div>
            {historyLoading ? (
              <SkeletonLoader type="card" />
            ) : latestOokla ? (
              <>
                <p class="speedtest__result-value">
                  ↓ {formatBps(latestOokla.download)}
                </p>
                <p class="speedtest__result-detail">
                  ↑ {formatBps(latestOokla.upload)} · {formatMs(latestOokla.ping)}
                </p>
                <p class="speedtest__result-meta">
                  {latestOokla.server_name}
                </p>
                <p class="speedtest__result-meta">
                  {formatTimestamp(latestOokla.timestamp)} (
                  {formatTimeAgo(latestOokla.timestamp)})
                </p>
              </>
            ) : (
              <p class="speedtest__empty">Belum ada data</p>
            )}
          </div>
        </div>
      </section>

      <section class="speedtest__controls">
        <TimeRangePicker
          value={range}
          onChange={handleRangeChange}
          options={[...SPEEDTEST_RANGES]}
        />
        <LastUpdated timestamp={lastTimestamp} />
      </section>

      <section class="speedtest__section card">
        <h2 class="speedtest__section-title">Kecepatan Download</h2>
        <SpeedtestChart
          data={speedtestData}
          type="download"
          loading={historyLoading}
          error={historyError?.message}
        />
      </section>

      <section class="speedtest__section card">
        <h2 class="speedtest__section-title">Kecepatan Upload</h2>
        <SpeedtestChart
          data={speedtestData}
          type="upload"
          loading={historyLoading}
          error={historyError?.message}
        />
      </section>

      <section class="speedtest__section card">
        <h2 class="speedtest__section-title">Latency (Ping)</h2>
        <SpeedtestChart
          data={speedtestData}
          type="ping"
          loading={historyLoading}
          error={historyError?.message}
        />
      </section>
    </div>
  );
}
